import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'
import gsap from 'gsap'

import { useMedia } from 'utils/hooks'

import media from 'styles/media'
import colors from 'styles/colors'

import ArrowSVG from "assets/images/icons/bitArrow.svg";

const SEQUENCE_LENGTH = 241

const sequence = {
  frame: 0
}

type Props = {
  loaded: boolean
}

const Reveal: React.FC<Props> = ({ loaded }) => {
  const images = useRef<any[]>([])
  const overlayRef = useRef<HTMLDivElement | null>(null)

  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null)
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null)
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null)

  const folder = useMedia("sequence-desktop", "sequence-desktop", 'sequence-responsive', 'sequence-responsive')
  const canvasWidth = useMedia(1440, 1440, 1024, 1024)
  const canvasHeight = useMedia(800, 800, 1366, 1366)
  const downArrowOffset = useMedia(
    "200, -4100",
    "0, -4240",
    "0, -1900",
    "0, -1800",
  )

  const preloadImages = useCallback(() => {
    if (folder) {
      for (let i = 1; i < SEQUENCE_LENGTH; i++) {
        import(`assets/images/${folder}/reveal-${(`00${i}`).slice(-3)}.webp`).then(image => {
          const img = new Image();
          img.src = image.default;
          images.current[i] = img
        });
      }
    }
  }, [folder])

  const render = useCallback(() => {
    if (context && canvasRef) {
      context.clearRect(0, 0, canvasRef.width, canvasRef.height);
      context.drawImage(images.current[sequence.frame], 0, 0, canvasWidth, canvasHeight); 
    }
  }, [canvasRef, context, canvasWidth, canvasHeight])

  useEffect(() => {
    if (loaded) {
      preloadImages()
    } 
  }, [loaded, preloadImages])

  useEffect(() => {
    if (!loaded) {
      if (context && canvasRef) {
        import(`assets/images/${folder}/reveal-000.webp`).then(image => {
          const img = new Image();
          img.src = image.default;
          images.current[0] = img

          img.onload = () => {
            context.clearRect(0, 0, canvasRef.width, canvasRef.height);
            context.drawImage(img, 0, 0, canvasWidth, canvasHeight); 
          }
        });
      }
    }
  }, [loaded, context, canvasWidth, canvasHeight, canvasRef, folder])

  useEffect(() => {
    if (canvasRef) {
      setContext(canvasRef.getContext('2d'))
      canvasRef.width = canvasWidth
      canvasRef.height = canvasHeight
    }
  }, [canvasRef, canvasHeight, canvasWidth])

  useEffect(() => {
    if (wrapperRef) {
      const tl = gsap.timeline({
        scrollTrigger: {
          scroller: ".smooth-scroll",
          trigger: wrapperRef,
          scrub: 0.5,
          start: "top top",
          end: "bottom bottom"
        }
      })

      tl.to(sequence, {
        frame: SEQUENCE_LENGTH - 1,
        snap: "frame",
        ease: "none",
        onUpdate: render // use animation onUpdate instead of scrollTrigger's onUpdate
      });

      const tl2 = gsap.timeline({
        scrollTrigger: {
          scroller: ".smooth-scroll",
          trigger: wrapperRef,
          scrub: true,
          start: "top bottom",
          end: "top top"
        }
      })

      tl2.to(overlayRef.current, {
        opacity: 0,
        ease: "circ.in"
      })

      return () => {
        tl.kill()
      }
    }
  }, [wrapperRef, render])

  return (
    <Wrapper ref={ref => setWrapperRef(ref)} id="reveal-wrapper">
      <Overlay ref={overlayRef}/>
      <Inner
        data-scroll
        data-scroll-sticky
        data-scroll-target="#reveal-wrapper"
        data-scroll-offset="0, 0"
      >
        <Canvas ref={ref => setCanvasRef(ref)}/>
      </Inner>
      <DownArrow 
        src={ArrowSVG} 
        alt="bit arrow" 
        loading="lazy"
        data-scroll
        data-scroll-sticky
        data-scroll-target="#hero-wrapper"
        data-scroll-offset={downArrowOffset}
      />
    </Wrapper>
  )
}

export default Reveal

const Wrapper = styled.section`
  height: 5000px;
  position: relative;

  ${media.tablet} {
    height: 3000px;
  }

  ${media.mobile} {
    height: 2500px;
  }
`

const Inner = styled.div`
  position: absolute;
  top: 0vw;
  left: 50%;
  aspect-ratio: 9 / 5;
  min-width: 100vw;
  min-height: 100vh;

  ${media.tablet} {
    aspect-ratio: 512 / 683;
  }

  ${media.mobile} {
    aspect-ratio: 512 / 683;
  }
`

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  transform: translateX(-50%);
`

const Overlay = styled.div`
  background-color: ${colors.pureBlack};
  width: 100vw;
  height: 100vh;
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  opacity: 0.8;
`

const DownArrow = styled.img`
  position: absolute;
  z-index: 8;
  transform: rotate(0);
  display: block;

  top: -11.46vw;
  left: calc(50% - (1.53vw / 2));
  height: 2.08vw;
  width: 1.53vw;

  ${media.tablet} {
    top: -13vw;
    left: calc(50% - (2.93vw / 2));
    width: 2.93vw;
    height: 2.148vw;
  }

  ${media.mobile} {
    top: -1vw;
    left: calc(50% - (8vw / 2));
    width: 8vw;
    height: 5.867vw;
  }
`;